/**
 * AccountRow — строка раздела «Аккаунты»: человек, телефон, клиники с
 * ролями (исключённые — зачёркнуты), блокировка с причиной и кнопка
 * «Заблокировать» / «Разблокировать». Себя заблокировать нельзя.
 */

import dayjs from 'dayjs';
import {Lock, ShieldCheck, Unlock} from 'lucide-react';

import type {Account} from '@services/accounts';
import {Button} from '@components/ui/Button';
import {cn} from '@lib/utils';
import {formatKgPhone} from '@utils/phone';

type Props = {
  account: Account;
  /** Это я — кнопки блокировки нет */
  self: boolean;
  pending: boolean;
  onBlock: (a: Account) => void;
  onUnblock: (a: Account) => void;
  last: boolean;
};

/** «+996700111222» → «+996 700 111 222»; чужие коды — как есть */
const showPhone = (phone: string): string =>
  phone.startsWith('+996') ? `+996 ${formatKgPhone(phone.slice(4))}` : phone;

const initials = (name: string): string =>
  name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

export const AccountRow = ({account: a, self, pending, onBlock, onUnblock, last}: Props) => {
  const handleBlock = () => onBlock(a);
  const handleUnblock = () => onUnblock(a);

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-[13px]',
        !last && 'border-b border-skr-hairline',
        a.blocked && 'bg-destructive/[0.03]',
      )}
    >
      <span
        className={cn(
          'grid size-[38px] shrink-0 place-items-center rounded-full text-[13px] font-semibold',
          a.isPlatformAdmin ? 'bg-accent text-primary' : 'bg-skr-icon-bg text-muted-foreground',
        )}
      >
        {initials(a.fullName)}
      </span>

      <div className="min-w-[180px] flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold">{a.fullName}</span>
          {a.isPlatformAdmin && (
            <span className="inline-flex items-center gap-1 rounded-full bg-accent px-2 py-px text-[11px] font-semibold text-primary">
              <ShieldCheck className="size-3" strokeWidth={2.2} />
              Команда Skrezo
            </span>
          )}
        </div>
        <div className="mt-0.5 font-mono text-xs text-muted-foreground">{showPhone(a.phone)}</div>
        {a.blocked && (
          <div className="mt-1 text-xs text-skr-danger">
            Заблокирован
            {a.blockedAt !== null
              ? ` ${dayjs(a.blockedAt).format('D MMM YYYY').replace('.', '')}`
              : ''}
            {a.blockedReason !== null ? ` · ${a.blockedReason}` : ''}
          </div>
        )}
      </div>

      <div className="flex min-w-[160px] flex-[1.3] flex-wrap gap-1.5">
        {a.memberships.length === 0 ? (
          <span className="text-xs text-skr-faint">{a.isPlatformAdmin ? '' : 'Нет клиник'}</span>
        ) : (
          a.memberships.map((m) => (
            <span
              key={m.membershipId}
              title={m.active ? undefined : 'Исключён из клиники'}
              className={cn(
                'rounded-full border border-skr-line px-2 py-0.5 text-[11.5px]',
                m.active ? 'text-skr-ink2' : 'text-skr-faint line-through',
              )}
            >
              {m.clinicName} · {m.isOwner ? 'Владелец' : m.roleName}
            </span>
          ))
        )}
      </div>

      {!self &&
        (a.blocked ? (
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5"
            disabled={pending}
            onClick={handleUnblock}
          >
            <Unlock className="size-3.5" strokeWidth={2} />
            Разблокировать
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 text-destructive hover:text-destructive"
            disabled={pending}
            onClick={handleBlock}
          >
            <Lock className="size-3.5" strokeWidth={2} />
            Заблокировать
          </Button>
        ))}
    </div>
  );
};
